<template>
  <div
    class="w-full h-full px-4 tablet-h:px-5 pt-[14px] pb-[22px] bg-gray-200/20 rounded-xl"
  >
    <div class="flex items-center gap-x-2.5 mb-2">
      <component :is="icon" class="size-[22px] text-gray-800" />

      <div class="font-medium text-[15px] leading-[20px] text-gray-800">
        {{ title }}
      </div>
    </div>

    <div class="w-full h-px mb-4 bg-gray-200"></div>

    <MatchMarkers
      v-if="variant === 'stem'"
      :markers="match.markers"
      :side="type === 'original' ? 'registered' : 'infringing'"
    />

    <div class="text-[12px] leading-[16px] text-gray-500 mb-[3px]">
      {{ matchTitle }}
    </div>

    <TickerTimeline :duration="Number(match.duration)" class="mb-[13px]" />

    <Wave
      :url="match.amplitudeUrl"
      :stem="stem"
      :regions="regions"
      :type="type"
      :level="match.level ?? undefined"
      :label="props.number"
      class="relative z-10 mb-[25px]"
      @pill-center="pillCenterPct = $event"
    />

    <MatchBracket
      :marker-pct="pillCenterPct ?? markerPosition"
      :leg-x="bracketLegX"
      :level="type === 'original' ? null : match.level"
      class="mb-[3px]"
    />

    <div class="flex items-center gap-x-3 tablet-h:gap-x-5">
      <div
        v-if="canPlayMatch"
        class="min-w-9 h-9 border border-gray-200 rounded-full flex items-center justify-center cursor-pointer bg-white"
        @click="handleClick"
      >
        <component
          :is="playbackIcon"
          class="size-4 text-gray-800"
          :style="{
            marginLeft: playbackIcon === PlayIcon ? '3px' : null,
          }"
        />
      </div>

      <div
        class="w-full h-[50px] flex items-center justify-center border-2 border-y-0 rounded-[3px]"
        :class="{
          'border-x-gray-500 bg-gray-500/10': type === 'original',
          ...(type === 'infringing' && {
            'border-x-red-500 bg-red-500/10': match.level === ScoreLevel.High,
            'border-x-yellow-500 bg-yellow-500/10':
              match.level === ScoreLevel.Medium,
            'border-x-green-500 bg-green-500/10':
              match.level === ScoreLevel.Low,
          }),
        }"
      >
        <div v-if="canPlayMatch" ref="audioEl" class="w-full"></div>
        <canvas v-else ref="isolatedWaveCanvas" class="w-full h-10"></canvas>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { graphql } from '@/shared/graphql/router'

export const AudioMatchFragment = graphql(`
  fragment AudioMatchFields on AudioMatch {
    id
    location
    summary
    level
    matchingUrl
    amplitudeUrl
    start
    end
    duration
    markers {
      hash
      color
      shine
      visible
    }
    stem {
      name
      url
    }
  }
`)
</script>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { VocalAndInstrumentalIcon, PlayIcon, PauseIcon } from '@dacr/icons'
import { stemIcon } from '@/features/copyright/utils/stem'
import Wave, { type Region } from '@/features/copyright/components/Wave.vue'
import MatchBracket from './MatchBracket.vue'
import MatchMarkers from './MatchMarkers.vue'
import TickerTimeline from './TickerTimeline.vue'
import { useDevice } from '@/shared/composables/useDevice'
import {
  fetchWaveformData,
  type WaveformStem,
} from '@/features/copyright/utils/waveform'
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  type Component,
} from 'vue'

const props = defineProps<{
  match: FragmentType<typeof AudioMatchFragment>
  variant: 'original' | 'stem'
  number: number
  allowPlayback?: boolean
}>()

const match = useFragment(AudioMatchFragment, props.match)

const canPlayMatch = computed(() => props.allowPlayback !== false)

const type = computed(() => (match.summary ? 'infringing' : 'original'))

const title = computed(() =>
  props.variant === 'original' ? 'Original audio' : match.stem.name
)

const stem = computed(() =>
  props.variant === 'stem' ? match.stem.name.toLowerCase() : undefined
)

const matchTitle = computed(() =>
  props.variant === 'stem' ? 'Isolated match' : 'Matching segment'
)

const url = computed(() =>
  props.variant === 'original' ? match.matchingUrl : match.stem.url
)

const icon = computed<Component>(() =>
  stem.value ? stemIcon(stem.value) : VocalAndInstrumentalIcon
)

const regions = computed<Region[]>(() => [
  {
    id: match.id,
    start: Number(match.start),
    end: Number(match.end),
    label: match.location,
    type:
      match.level === ScoreLevel.High
        ? 'danger'
        : match.level === ScoreLevel.Medium
          ? 'warning'
          : 'normal',
  },
])

// Where Wave's (edge-clamped) location pill landed; the bracket meets it.
const pillCenterPct = ref<number | null>(null)

const markerPosition = computed(
  () =>
    ((Number(match.start) + Number(match.end)) / 2 / Number(match.duration)) *
    100
)

const { viewportWidth } = useDevice()

const bracketLegX = computed(() => {
  if (!canPlayMatch.value) return 0
  return viewportWidth.value >= 1024 ? 56 : 48
})

const wavesurfer = ref<InstanceType<typeof import('wavesurfer.js').default>>()
let playbackMedia: HTMLAudioElement | null = null
const audioEl = ref<HTMLElement>()
const isolatedWaveCanvas = ref<HTMLCanvasElement>()
const playbackIcon = shallowRef<Component>(PlayIcon)
let isolatedWaveResizeObserver: ResizeObserver | null = null
let isolatedWaveResizeTimeout: ReturnType<typeof setTimeout> | null = null

const handleClick = () => {
  if (!canPlayMatch.value) return
  wavesurfer.value?.playPause()
  playbackIcon.value = wavesurfer.value?.isPlaying() ? PauseIcon : PlayIcon
}

const getWaveColor = () => {
  if (type.value === 'original') {
    return '#6E7480'
  }

  if (match.level === ScoreLevel.High) {
    return 'rgba(238, 136, 138)'
  }

  if (match.level === ScoreLevel.Medium) {
    return 'rgba(245, 185, 88)'
  }

  return 'rgba(96, 204, 214)'
}

const getProgressColor = () => {
  if (type.value === 'original') {
    return '#454b54'
  }

  if (match.level === ScoreLevel.High) {
    return '#DC3C3F'
  }

  if (match.level === ScoreLevel.Medium) {
    return '#CE773A'
  }

  return '#1B95AB'
}

const getIsolatedWaveStem = (data: {
  main: WaveformStem | null
  stems: WaveformStem[]
}) => {
  if (props.variant === 'original') return data.main
  return data.stems.find((s) => s.name === stem.value) ?? null
}

const getStemPointTime = (stemData: WaveformStem, index: number) =>
  stemData.timepoints[index] ??
  (index / Math.max(1, stemData.max.length - 1)) * stemData.duration

const findSegmentRange = (stemData: WaveformStem) => {
  const start = Number(match.start)
  const end = Number(match.end)
  let first = -1
  let final = -1
  for (let index = 0; index < stemData.max.length; index++) {
    const time = getStemPointTime(stemData, index)
    if (first === -1 && time >= start) first = index
    if (time <= end) final = index
  }
  if (first === -1) first = 0
  if (final === -1) final = stemData.max.length - 1
  return { first, final }
}

const loadMatchPeaks = async () => {
  try {
    const stemData = getIsolatedWaveStem(
      await fetchWaveformData(match.amplitudeUrl)
    )
    if (!stemData) return null
    const { first, final } = findSegmentRange(stemData)
    if (final < first) return null
    const duration = Number(match.end) - Number(match.start)
    if (!(duration > 0)) return null
    return {
      peaks: [
        stemData.max.slice(first, final + 1),
        stemData.min.slice(first, final + 1),
      ],
      duration,
    }
  } catch {
    return null
  }
}

const drawIsolatedWaveform = (stemData: WaveformStem) => {
  const canvas = isolatedWaveCanvas.value
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  const rect = canvas.getBoundingClientRect()
  if (!ctx || !rect.width || !rect.height) return

  const dpr = window.devicePixelRatio || 1
  const width = Math.max(1, Math.round(rect.width * dpr))
  const height = Math.max(1, Math.round(rect.height * dpr))
  canvas.width = width
  canvas.height = height
  ctx.clearRect(0, 0, width, height)

  const { first, final } = findSegmentRange(stemData)
  const sampleCount = final - first + 1
  if (sampleCount <= 0) return

  const centerY = Math.round(height / 2)
  const bucketsPerColumn = sampleCount / width
  ctx.fillStyle = getWaveColor()

  for (let x = 0; x < width; x++) {
    const bucketStart = first + Math.floor(x * bucketsPerColumn)
    const bucketEnd = Math.min(
      final + 1,
      Math.max(bucketStart + 1, first + Math.floor((x + 1) * bucketsPerColumn))
    )

    let max = 0
    let min = 0
    for (let index = bucketStart; index < bucketEnd; index++) {
      max = Math.max(max, stemData.max[index] ?? 0)
      min = Math.min(min, stemData.min[index] ?? 0)
    }

    const top = Math.round(max * centerY)
    const bottom = Math.round(-min * (height - centerY))
    if (top > 0) ctx.fillRect(x, centerY - top, 1, top)
    if (bottom > 0) ctx.fillRect(x, centerY, 1, bottom)
  }
}

const renderIsolatedWaveform = async () => {
  const stemData = getIsolatedWaveStem(
    await fetchWaveformData(match.amplitudeUrl)
  )
  if (!stemData) return

  await nextTick()
  drawIsolatedWaveform(stemData)

  if (!isolatedWaveCanvas.value) return
  isolatedWaveResizeObserver = new ResizeObserver(() => {
    if (isolatedWaveResizeTimeout) clearTimeout(isolatedWaveResizeTimeout)
    isolatedWaveResizeTimeout = setTimeout(() => {
      drawIsolatedWaveform(stemData)
    }, 100)
  })
  isolatedWaveResizeObserver.observe(isolatedWaveCanvas.value)
}

onMounted(async () => {
  if (!canPlayMatch.value) {
    renderIsolatedWaveform()
    return
  }
  if (!audioEl.value) return

  const [{ default: WaveSurfer }, segment] = await Promise.all([
    import('wavesurfer.js'),
    loadMatchPeaks(),
  ])
  if (!audioEl.value) return

  playbackMedia = document.createElement('audio')
  playbackMedia.preload = 'none'

  wavesurfer.value = WaveSurfer.create({
    container: audioEl.value,
    media: playbackMedia,
    waveColor: getWaveColor(),
    progressColor: getProgressColor(),
    url: url.value,
    peaks: segment?.peaks,
    duration: segment?.duration,
    height: 40,
    cursorWidth: 0,
  })
})

onUnmounted(() => {
  isolatedWaveResizeObserver?.disconnect()
  isolatedWaveResizeObserver = null
  if (isolatedWaveResizeTimeout) {
    clearTimeout(isolatedWaveResizeTimeout)
    isolatedWaveResizeTimeout = null
  }
  wavesurfer.value?.destroy()
  wavesurfer.value = undefined
  if (playbackMedia) {
    playbackMedia.pause()
    playbackMedia.removeAttribute('src')
    playbackMedia.load()
    playbackMedia = null
  }
})
</script>
